import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	KnowledgeDiscoverySearchChatIntentDetectionModelVersion,
	type KnowledgeDiscoverySearchChatIntentDetectionModelVersionType,
} from '@atlassian/search-client/intent-detection';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';

import { PrimaryProductKeys } from '../../constants/primary-product-keys';

export type ChatSurface = 'new-tab' | 'sidebar' | 'floaty' | 'full-page';

const CHAT_CONVERGENCE_EXPERIMENT = 'rovo_search_chat_convergence_experiment';

/**
 * Resolves which chat convergence surface should be served.
 *
 * The overlapping experiment cohorts map to the implemented surfaces:
 *
 *   arm-a -> sidebar
 *   arm-b -> floaty
 *   arm-c -> new tab
 *   arm-d -> floaty with the experimental intent-detection model
 *
 * All legacy feature gates are evaluated before the experiment. If any gate is enabled, its
 * surface is served without evaluating the experiment and recording its exposure. When multiple
 * gates are enabled, the existing order is retained: new tab > sidebar > floaty. Legacy gates and
 * arms A-C use the baseline intent-detection model.
 */
export const serveChatConvergenceExperiment = (
	primaryProduct: string | undefined,
): {
	chatIntentDetectionEnabled: boolean;
	chatSurface: ChatSurface | undefined;
	intentDetectionModel: KnowledgeDiscoverySearchChatIntentDetectionModelVersionType | undefined;
} => {
	const isSearchChatConvergenceProduct = primaryProduct === PrimaryProductKeys.Confluence;

	if (!isSearchChatConvergenceProduct) {
		return {
			chatIntentDetectionEnabled: false,
			chatSurface: undefined,
			intentDetectionModel: undefined,
		};
	}

	const sidebarChatEnabled = fg('rovo_search_chat_convergence_sidebar');
	const floatyChatEnabled = fg('rovo_search_chat_convergence_floaty');
	const newTabChatEnabled = fg('rovo_search_chat_convergence_tab');

	if (newTabChatEnabled) {
		return {
			chatIntentDetectionEnabled: true,
			chatSurface: 'new-tab',
			intentDetectionModel:
				KnowledgeDiscoverySearchChatIntentDetectionModelVersion.GEMMA3_1B_SCI_V1,
		};
	}

	if (sidebarChatEnabled) {
		return {
			chatIntentDetectionEnabled: true,
			chatSurface: 'sidebar',
			intentDetectionModel:
				KnowledgeDiscoverySearchChatIntentDetectionModelVersion.GEMMA3_1B_SCI_V1,
		};
	}

	if (floatyChatEnabled) {
		return {
			chatIntentDetectionEnabled: true,
			chatSurface: 'floaty',
			intentDetectionModel:
				KnowledgeDiscoverySearchChatIntentDetectionModelVersion.GEMMA3_1B_SCI_V1,
		};
	}

	const { cohort } = getFrontendExperiment({ abTestId: CHAT_CONVERGENCE_EXPERIMENT });
	let chatSurface: ChatSurface | undefined;
	let intentDetectionModel: KnowledgeDiscoverySearchChatIntentDetectionModelVersionType | undefined;
	switch (cohort) {
		case 'arm-c':
			chatSurface = 'new-tab';
			intentDetectionModel =
				KnowledgeDiscoverySearchChatIntentDetectionModelVersion.GEMMA3_1B_SCI_V1;
			break;
		case 'arm-a':
			chatSurface = 'sidebar';
			intentDetectionModel =
				KnowledgeDiscoverySearchChatIntentDetectionModelVersion.GEMMA3_1B_SCI_V1;
			break;
		case 'arm-b':
			chatSurface = 'floaty';
			intentDetectionModel =
				KnowledgeDiscoverySearchChatIntentDetectionModelVersion.GEMMA3_1B_SCI_V1;
			break;
		case 'arm-d':
			chatSurface = 'floaty';
			intentDetectionModel =
				KnowledgeDiscoverySearchChatIntentDetectionModelVersion.GEMMA4_E2B_SCI_V1;
			break;
	}

	return {
		chatIntentDetectionEnabled: chatSurface !== undefined,
		chatSurface,
		intentDetectionModel,
	};
};
